import { useEffect, useMemo, useRef, useState } from "react"
import { v4 as randomUUID } from "uuid"
import { getDefaultCommentCreateArgs } from "@nova/serializer/src/client/validators"
import { addEntitySocket, deleteEntitySocket } from "features/entity-caching/project/slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectCommentPlacementState } from "features/entity-caching/map-comments/selectors"
import { selectUserID } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { NovaMarker } from "features/map/NovaMarker"
import { useCreateComment } from "features/comments/Comments"
import { UserCommentBubble } from "./CommentBubble"
import { CommentInput } from "./CommentInput"
import { setCommentPlacingState } from "./CommentSlice"
import { useCommentImageUpload } from "./useCommentImageUpload"

export const CommentDraftMarkerGate = () => {
    const placement = useAppSelector(selectCommentPlacementState)

    if (placement.mode !== "writing") return null
    return <CommentDraftMarker lat={placement.lat} lng={placement.lng} />
}

const CommentDraftMarker = ({ lat, lng }: { lat: number; lng: number }) => {
    const dispatch = useAppDispatch()

    const userId = useAppSelector(selectUserID)!
    const [value, setValue] = useState("")
    const markerRef = useRef<HTMLDivElement>(null)
    const commentId = useMemo(() => randomUUID(), [])
    const imageUpload = useCommentImageUpload(commentId)
    const { createComment } = useCreateComment()

    // Escape discards the draft and drops back to placing, even while the editor has focus.
    // A second Escape is handled by the portal and exits the tool entirely.
    useEffect(() => {
        const discardDraft = () => dispatch(setCommentPlacingState({ mode: "placing" }))
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") discardDraft()
        }
        // Clicking the map away from the draft discards it too. Portalled popups
        // (emoji picker, mention list) live outside the marker, so ignore those.
        const onMouseDown = (event: MouseEvent) => {
            const target = event.target as Element
            if (markerRef.current?.contains(target) || target.closest("#portal")) return
            discardDraft()
        }
        window.addEventListener("keydown", onKeyDown)
        document.getElementById("map")?.addEventListener("mousedown", onMouseDown, true)
        return () => {
            window.removeEventListener("keydown", onKeyDown)
            document.getElementById("map")?.removeEventListener("mousedown", onMouseDown, true)
        }
    }, [dispatch])

    return (
        <NovaMarker lat={lat} lng={lng} anchor="bottom-left">
            <div ref={markerRef} className="flex items-end gap-2">
                <UserCommentBubble userId={userId} />
                <div className="w-[316px] rounded-lg bg-gray-900 drop-shadow-[0px_2px_5px_rgba(12,12,15,0.95)]">
                    <CommentInput
                        value={value}
                        onChange={setValue}
                        imageUpload={imageUpload}
                        onSubmit={async () => {
                            dispatch(
                                addEntitySocket({
                                    data: {
                                        ...getDefaultCommentCreateArgs(),
                                        id: commentId,
                                        longitude: lng,
                                        latitude: lat,
                                        createdBy: { userId },
                                    },
                                    path: ["mapComments"],
                                })
                            )
                            // Stay in comment mode so the panel falls back to the full list.
                            dispatch(setCommentPlacingState({ mode: "placing" }))
                            try {
                                await createComment({
                                    comment: value,
                                    context: { target: "map-pin" },
                                    entityId: commentId,
                                    imageFileIds: imageUpload.uploadedFileIds,
                                    position: { longitude: lng, latitude: lat },
                                })
                            } catch {
                                dispatch(
                                    deleteEntitySocket({ path: ["mapComments", { id: commentId }] })
                                )
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: "Failed to post comment. Try again.",
                                        status: GlobalNotificationStatus.error,
                                        timeoutInMs: 5000,
                                    })
                                )
                            }
                        }}
                    />
                </div>
            </div>
        </NovaMarker>
    )
}
